import { DealerSpaceParameterKey } from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import {
  SPACE_DATE_RANGE_PANEL_VALUES,
  defineSpaceParameterPanelValues,
  useSpaceParameterizedFilter,
} from '@src/components/AppLayout/SpaceParameters';
import {
  FilterClauseType,
  cleanupInitialFilterDateRange,
  removeEmptyFilters,
  useCurrenciesFilter,
  useCustomerTransactionStatusesFilter,
  useCustomerTransactionTypesFilter,
  useDateRangeFilter,
  type BlotterTableFiltersProps,
  type CustomerTransaction,
  type FilterClause,
  type FilterableProperty,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isArray, isEqual, keys } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import { useCounterpartiesFilter } from '../../../hooks';
import type { CustomerTransactionsFilter } from './types';

export interface UseCustomerTransactionsFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}

export const CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT = defineSpaceParameterPanelValues({
  [DealerSpaceParameterKey.Counterparty]: 'Counterparties',
  [DealerSpaceParameterKey.DateRange]: SPACE_DATE_RANGE_PANEL_VALUES,
  [DealerSpaceParameterKey.Currencies]: 'Currencies',
});
export const CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_KEYS = CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT.keys;

export function useCustomerTransactionsFilter({ persistedBlotterTable }: UseCustomerTransactionsFilterParams) {
  const { onFilterChanged: saveFilter } = persistedBlotterTable;
  const [initialFilter] = useState(() => cleanupInitialFilterDateRange(persistedBlotterTable.initialState?.filter));
  const [localFilter, setFilter] = useState<CustomerTransactionsFilter>(
    (initialFilter as CustomerTransactionsFilter) || {}
  );

  const changeLocalFilter = useCallback(
    (action: SetStateAction<CustomerTransactionsFilter>) => {
      const priorFilter = localFilter;
      const newFilter = action instanceof Function ? action(localFilter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [localFilter, saveFilter]
  );
  const { changeFilter, controlledKeys, filter, getSelfManagedFilterBuilderProps } = useSpaceParameterizedFilter({
    filter: localFilter,
    changeFilter: changeLocalFilter,
    spaceParameterPanelValues: CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
  });
  const isDateRangeControlled = controlledKeys.includes(DealerSpaceParameterKey.DateRange);

  const currenciesFilter = useCurrenciesFilter();
  const statusesFilter = useCustomerTransactionStatusesFilter();
  const transactionTypesFilter = useCustomerTransactionTypesFilter();
  const counterpartiesFilter = useCounterpartiesFilter();

  const filterableProperties: FilterableProperty<keyof CustomerTransactionsFilter>[] = useMemo(
    () => [currenciesFilter, statusesFilter, transactionTypesFilter, counterpartiesFilter],
    [currenciesFilter, statusesFilter, transactionTypesFilter, counterpartiesFilter]
  );

  const handleFilterClausesChanged = useCallback(
    (filterClausesByPropertyKey: Map<string, FilterClause>, propertiesByKey: Map<string, FilterableProperty>) => {
      changeFilter(curr => {
        const newFilter = removeEmptyFilters<CustomerTransactionsFilter>({
          ...curr,
          ...(Object.fromEntries(
            [...propertiesByKey.keys()].map(key => [key, filterClausesByPropertyKey.get(key)?.selections])
          ) satisfies CustomerTransactionsFilter),
        });
        if (isEqual(curr, newFilter)) {
          return curr;
        }
        return newFilter;
      });
    },
    [changeFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof CustomerTransactionsFilter)[]).forEach((key: keyof CustomerTransactionsFilter) => {
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: (isArray(filter[key]) ? filter[key] : compact([filter[key]])) as string[],
        });
      });
    }
    return clauses;
  }, [filter]);

  const dateRangeFilter = useDateRangeFilter(filter, changeFilter);

  const filterBuilderProps = useMemo(
    () =>
      getSelfManagedFilterBuilderProps({
        initialFilterClauses,
        properties: filterableProperties,
        onFilterClausesChanged: handleFilterClausesChanged,
      }),
    [getSelfManagedFilterBuilderProps, initialFilterClauses, filterableProperties, handleFilterClausesChanged]
  );
  const blotterTableFilterProps = useMemo(
    () => (isDateRangeControlled ? { showDateRangePicker: false } : dateRangeFilter),
    [dateRangeFilter, isDateRangeControlled]
  ) satisfies Partial<BlotterTableFiltersProps>;

  return {
    initialFilter,
    filter,
    changeFilter,
    contextMenuFilterableProperties: filterableProperties,
    // shortcut to spread properties into useAccordionFilterBuilder.filterBuilderProps
    filterBuilderProps,
    // shortcut to spread props into the BlotterTableFilters component
    blotterTableFilterProps,
  };
}

const colIDToFilterBuilderKeyMap = {
  Counterparty: 'Counterparties',
  Currency: 'Currencies',
  Status: 'Statuses',
  TransactionType: 'TransactionTypes',
} satisfies Partial<Record<keyof CustomerTransaction, keyof CustomerTransactionsFilter>>;
export function colIDToFilterBuilderKey(id: string): keyof CustomerTransactionsFilter | undefined {
  return colIDToFilterBuilderKeyMap[id as keyof typeof colIDToFilterBuilderKeyMap] as
    | keyof CustomerTransactionsFilter
    | undefined;
}
